import { Link } from "react-router-dom";
import { MessageSquare, FileText, CalendarCheck, ShieldCheck, Clock, DollarSign, Handshake, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useSeo, useSite } from "../lib/site";
import { SERVICE_CATS, CallButton, QuoteButton, TextButton, SeniorBanner, FinalCta } from "../components/site/Blocks";
import { GalleryGrid, Testimonials } from "./Info";
import { AREA_LINE, OUTSIDE_NOTE, CITIES } from "../lib/cities";

export const FAQ = [
  ["Do I need to send photos to get an estimate?", "No. Photos help us estimate, but they're optional. You can call or text us and describe the job instead."],
  ["Is my preferred date a confirmed appointment?", "Not yet. The date you choose is a request. We confirm appointments personally after you approve a written estimate."],
  ["How does pricing work?", "We review your request and send a written, itemized estimate. Nothing is scheduled or charged until you approve it. If the job changes, we send a revised estimate for your approval."],
  ["How does the senior discount work?", "Senior customers get 20% off eligible services for life. Your written estimate shows exactly which charges the discount applies to and how much you save. It's a percentage discount, not a price freeze."],
  ["Can I arrange help for my parent?", "Yes. Tell us what's needed. With their permission, we can coordinate appointments with you and share completion updates."],
  ["Do you do electrical, plumbing, gas, HVAC or structural work?", "No. We don't currently offer regulated trade work such as electrical, plumbing, gas, HVAC, structural repairs or demolition."],
  ["What can't you haul?", "We don't haul hazardous materials such as chemicals, paint, fuel, asbestos or medical waste. Tell us about anything unusual and we'll let you know."],
];

const STEPS = [
  [MessageSquare, "Tell Us", "Use the short online form, call, or text us a few photos of the job."],
  [FileText, "Get an Estimate", "We review your request and send a clear, itemized written estimate."],
  [CalendarCheck, "Approve & Schedule", "Once you approve, we confirm a time that works for you. No surprises."],
];

const WHY = [
  [Clock, "Dependable service", "We show up when we say we will and keep you updated."],
  [DollarSign, "Upfront pricing", "Itemized written estimates you approve before any work begins."],
  [Handshake, "Respectful treatment", "Patient, courteous help in your home — your way, at your pace."],
  [ShieldCheck, "Clear communication", "Plain answers by phone, text or email. Whatever is easiest for you."],
];

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#131413] text-white">
      <div className="wrap grid items-center gap-10 py-14 sm:py-20 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="fade-up">
          <p className="eyebrow text-[#ff4651]">Junk Gone. Repairs Done.</p>
          <h1 className="mt-3 text-5xl font-extrabold uppercase leading-[0.95] sm:text-6xl lg:text-7xl" data-testid="hero-headline">Your Home To-Do List, Knocked Out.</h1>
          <p className="mt-6 max-w-xl text-xl leading-relaxed text-neutral-200">Handyman help, junk removal, and property cleanups in Albuquerque—with clear pricing and personal service.</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <QuoteButton testid="hero-quote-btn" />
            <CallButton className="btn-ghost-light" testid="hero-call-btn" />
          </div>
          <p className="mt-4 text-base font-bold text-neutral-200" data-testid="hero-area-line">{AREA_LINE}</p>
          <p className="mt-5 text-base text-neutral-300">Prefer texting? <TextButton className="inline-flex items-center gap-1 font-bold text-white underline underline-offset-4" label="Text us photos" testid="hero-text-link" /></p>
        </div>
        <div className="fade-up hidden lg:block" style={{ animationDelay: "120ms" }}>
          <img src="/brand/mark.png" alt="One Punch Services fist and trash can logo" className="w-full" />
          <p className="mt-2 text-center font-display text-2xl font-bold uppercase tracking-wide">We Knock It Out for You.</p>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="wrap py-16 sm:py-20" data-testid="home-services">
      <p className="eyebrow">What we do</p>
      <h2 className="h2 mt-2">Help for the jobs on your list</h2>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICE_CATS.map(({ key, title, icon: Icon, blurb, to }) => (
          <Link key={key} to={to} className="group card flex flex-col transition-colors hover:border-[#D9232E]" data-testid={`service-card-${key}`}>
            <Icon className="h-10 w-10 text-[#D9232E]" aria-hidden="true" />
            <h3 className="mt-4 text-2xl font-bold uppercase">{title}</h3>
            <p className="mt-2 flex-1 text-base leading-relaxed text-neutral-700">{blurb}</p>
            <span className="mt-4 font-bold text-[#B81B25] group-hover:underline">Learn more →</span>
          </Link>
        ))}
        <div className="card flex flex-col justify-center bg-[#F5F5F5]">
          <p className="text-xl font-bold">Not sure what you need?</p>
          <p className="mt-2 text-base text-neutral-700">Describe it in your own words and we'll figure it out together.</p>
          <QuoteButton className="btn-dark mt-4" label="Start a request" testid="services-unsure-btn" />
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="bg-[#F5F5F5]" data-testid="home-process">
      <div className="wrap py-16 sm:py-20">
        <p className="eyebrow">How it works</p>
        <h2 className="h2 mt-2">Three simple steps</h2>
        <ol className="mt-10 grid gap-6 md:grid-cols-3">
          {STEPS.map(([Icon, t, d], i) => (
            <li key={t} className="card relative">
              <span className="font-display text-6xl font-extrabold text-[#D9232E]/20">{i + 1}</span>
              <Icon className="absolute right-6 top-6 h-8 w-8 text-[#D9232E]" aria-hidden="true" />
              <h3 className="text-2xl font-bold uppercase">{t}</h3>
              <p className="mt-2 text-base text-neutral-700">{d}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="wrap grid gap-10 py-16 sm:py-20 lg:grid-cols-[0.8fr_1.2fr]" data-testid="home-why">
      <div>
        <p className="eyebrow">Why One Punch</p>
        <h2 className="h2 mt-2">Locally owned. Straightforward. Respectful.</h2>
        <p className="mt-4 text-lg text-neutral-700">We're an Albuquerque-based business that treats your home and your time with respect.</p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        {WHY.map(([Icon, t, d]) => (
          <div key={t} className="border-l-4 border-[#D9232E] pl-5">
            <Icon className="h-7 w-7" aria-hidden="true" />
            <h3 className="mt-2 text-xl font-bold uppercase">{t}</h3>
            <p className="mt-1 text-base text-neutral-700">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function FaqList({ items = FAQ }) {
  return (
    <Accordion type="single" collapsible className="mt-8" data-testid="faq-list">
      {items.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`}>
          <AccordionTrigger className="py-5 text-left text-lg font-bold hover:no-underline" data-testid={`faq-trigger-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-base leading-relaxed text-neutral-700">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export default function Home() {
  const { site } = useSite();
  useSeo(null, "One Punch Services offers handyman help, junk removal, yard cleanup and property cleanouts in Albuquerque, Rio Rancho, Los Lunas, Corrales and Santa Fe, NM, with clear written estimates and 20% off eligible services for seniors for life.");
  return (
    <div data-testid="home-page">
      <Hero />
      <SeniorBanner />
      <Services />
      <Process />
      <Why />
      <section className="bg-[#F5F5F5]">
        <div className="wrap py-16 sm:py-20">
          <p className="eyebrow">Real projects</p>
          <h2 className="h2 mt-2">Before & After</h2>
          <GalleryGrid limit={6} />
          <Link to="/before-and-after" className="btn-outline mt-8" data-testid="home-gallery-link">See all projects</Link>
        </div>
      </section>
      {site.testimonials?.length > 0 && (
        <section className="wrap py-16">
          <p className="eyebrow">Customers</p>
          <h2 className="h2 mt-2">What customers say</h2>
          <Testimonials />
        </section>
      )}
      <section className="wrap grid gap-8 py-16 sm:py-20 md:grid-cols-2" data-testid="home-area">
        <div>
          <p className="eyebrow">Service area</p>
          <h2 className="h2 mt-2">Five cities, one local team</h2>
          <p className="mt-4 text-lg text-neutral-700" data-testid="home-area-line">{AREA_LINE}</p>
          <ul className="mt-4 flex flex-wrap gap-2">{CITIES.map((c) => <li key={c.slug}><Link to={`/service-areas/${c.slug}`} className="inline-flex min-h-[44px] items-center gap-1 rounded-full border-2 border-[#171717] px-4 font-bold hover:bg-[#171717] hover:text-white" data-testid={`home-city-${c.slug}`}><MapPin className="h-4 w-4" />{c.name}</Link></li>)}</ul>
          <p className="mt-4 text-base text-neutral-700">{OUTSIDE_NOTE}</p>
          <Link to="/service-areas" className="btn-outline mt-6" data-testid="home-area-link">Check your ZIP code</Link>
        </div>
        <div>
          <p className="eyebrow">FAQ</p>
          <h2 className="h2 mt-2">Common questions</h2>
          <FaqList items={FAQ.slice(0, 5)} />
        </div>
      </section>
      <FinalCta />
    </div>
  );
}
